/**
 * witnessFilterAxes — the filter-axis registry for the Witnesses tab's
 * SUGGESTIONS column. Mirrors inventoryFilterAxes.js exactly in shape so the
 * shared rail UI (FilterRail / FilterChip / AddFilterPicker) can drive it.
 *
 * Each axis is self-contained: how to enumerate its values from the witness
 * rows, how to match a row against a value, and how to render the value as a
 * chip label. New axes go HERE and nowhere else.
 *
 * IMPORTANT: facets are opt-in filters only. With no filters active, every
 * suggestion shows — groups, role-references, zero-evidence people included.
 * The rail lets the user surface (e.g.) "bar patrons"; it never auto-suppresses.
 *
 * Shape (identical to FILTER_AXES):
 *   { key, label, icon, getValues(rows, ctx)->[{value,label,count?}],
 *     matches(row, value, ctx)->bool, formatValue(value, ctx)->string }
 */

import { formatRole, formatQuality, QUALITY_LABEL } from '../domain/witnessEvidence';
import { filtersToParam, paramToFilters } from './inventoryFilterUrl';

const IMPORTANCE_OPTIONS = [
  { value: 'key', label: 'Key' },
  { value: 'standard', label: 'Standard' },
  { value: 'peripheral', label: 'Peripheral' },
];

// Quality values in a stable display order (real people first, junk last).
const QUALITY_ORDER = ['real_person', 'group', 'role_reference', 'junk', 'unclassified'];

function findOption(list, value) {
  return list.find((o) => o.value === value);
}

// ── Axis registry ──────────────────────────────────────────────────────

export const WITNESS_AXES = {
  // Entity type (LAW_ENFORCEMENT_OFFICER → "Officer", etc.) via formatRole.
  type: {
    key: 'type',
    label: 'Type',
    icon: '👤',
    getValues: (rows) => {
      const counts = {};
      for (const r of rows) {
        const t = r.role;
        if (!t) continue;
        counts[t] = (counts[t] || 0) + 1;
      }
      return Object.entries(counts)
        .map(([value, count]) => ({ value, label: formatRole({ role: value }), count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    },
    matches: (r, v) => r.role === v,
    formatValue: (v) => formatRole({ role: v }),
  },

  // Extractor quality classification (null → "unclassified").
  quality: {
    key: 'quality',
    label: 'Quality',
    icon: '🏷️',
    getValues: (rows) => {
      const counts = {};
      for (const r of rows) {
        const q = r.quality_label || 'unclassified';
        counts[q] = (counts[q] || 0) + 1;
      }
      return QUALITY_ORDER
        .filter((q) => counts[q])
        .map((q) => ({ value: q, label: QUALITY_LABEL[q], count: counts[q] }));
    },
    matches: (r, v) => (r.quality_label || 'unclassified') === v,
    formatValue: (v) => formatQuality(v),
  },

  // Derived: has any bound evidence vs. none.
  evidence: {
    key: 'evidence',
    label: 'Evidence',
    icon: '📎',
    getValues: (rows) => {
      let yes = 0, no = 0;
      for (const r of rows) {
        if ((r.evidence_count || 0) > 0) yes++;
        else no++;
      }
      const opts = [];
      if (yes) opts.push({ value: 'yes', label: 'Has evidence', count: yes });
      if (no) opts.push({ value: 'no', label: 'No evidence', count: no });
      return opts;
    },
    matches: (r, v) => (v === 'yes' ? (r.evidence_count || 0) > 0 : (r.evidence_count || 0) === 0),
    formatValue: (v) => (v === 'yes' ? 'Has evidence' : 'No evidence'),
  },

  // Triage band — key / standard / peripheral.
  importance: {
    key: 'importance',
    label: 'Importance',
    icon: '⭐',
    getValues: (rows) => {
      const counts = {};
      for (const r of rows) {
        const i = r.importance;
        if (!i) continue;
        counts[i] = (counts[i] || 0) + 1;
      }
      return IMPORTANCE_OPTIONS
        .filter((o) => counts[o.value])
        .map((o) => ({ value: o.value, label: o.label, count: counts[o.value] }));
    },
    matches: (r, v) => r.importance === v,
    formatValue: (v) => findOption(IMPORTANCE_OPTIONS, v)?.label || v,
  },

  // Duplicate risk — the resolver's dedup signal. Drives the cleanup workflow:
  // narrow to the people most likely to be the same before handing to Arc.
  // `dedup_confidence` buckets when present, else a synthetic "flagged" for a
  // bare `possible_duplicate_of`. Gracefully empty when no signal exists.
  duplicate: {
    key: 'duplicate',
    label: 'Duplicate risk',
    icon: '⚠️',
    getValues: (rows) => {
      const counts = {};
      for (const r of rows) {
        const conf = r.dedup_confidence;
        if (conf) counts[conf] = (counts[conf] || 0) + 1;
        else if (r.possible_duplicate_of) counts.flagged = (counts.flagged || 0) + 1;
      }
      const order = ['high', 'medium', 'low', 'flagged'];
      return order
        .filter((v) => counts[v])
        .map((v) => ({ value: v, label: DUPLICATE_LABEL[v] || v, count: counts[v] }));
    },
    matches: (r, v) =>
      v === 'flagged'
        ? !r.dedup_confidence && !!r.possible_duplicate_of
        : r.dedup_confidence === v,
    formatValue: (v) => DUPLICATE_LABEL[v] || v,
  },

  // Composition — already merged (binds >1 fragment) vs a single entity. Lets
  // the user target un-merged people for cleanup, or audit existing merges.
  composition: {
    key: 'composition',
    label: 'Composition',
    icon: '🧩',
    getValues: (rows) => {
      let merged = 0, single = 0;
      for (const r of rows) {
        if ((r.member_entity_ids?.length || 0) > 1) merged++;
        else single++;
      }
      const opts = [];
      if (merged) opts.push({ value: 'merged', label: 'Merged', count: merged });
      if (single) opts.push({ value: 'single', label: 'Single', count: single });
      return opts;
    },
    matches: (r, v) =>
      v === 'merged'
        ? (r.member_entity_ids?.length || 0) > 1
        : (r.member_entity_ids?.length || 0) <= 1,
    formatValue: (v) => (v === 'merged' ? 'Merged' : 'Single'),
  },
};

const DUPLICATE_LABEL = { high: 'High', medium: 'Medium', low: 'Low', flagged: 'Flagged' };

/** Stable axis order for the picker. */
export const WITNESS_AXIS_ORDER = ['type', 'quality', 'evidence', 'importance', 'duplicate', 'composition'];

// ── Shareable URL state ─────────────────────────────────────────────────
// Namespaced as `wf` (NOT inventory's `f`) so the two filter rails never clobber
// each other when the user switches tabs — the query string survives the
// path-param tab change. Reuses the same pure serializer format as inventory.
const WITNESS_FILTER_PARAM = 'wf';

/** Read the suggestions filters[] from the URL, dropping any unknown axis so a
 *  stray param can't inject a chip the registry can't render. */
export function readWitnessFiltersFromUrl() {
  if (typeof window === 'undefined') return [];
  const params = new URLSearchParams(window.location.search);
  return paramToFilters(params.get(WITNESS_FILTER_PARAM))
    .filter((f) => WITNESS_AXES[f.axis]);
}

/** Write the suggestions filters[] to the URL via replaceState (no reload). */
export function writeWitnessFiltersToUrl(filters) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const serialized = filtersToParam(filters);
  if (serialized) params.set(WITNESS_FILTER_PARAM, serialized);
  else params.delete(WITNESS_FILTER_PARAM);
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}

// Roster column has its OWN independent rail, namespaced `rf` so it never
// clobbers the suggestions rail (`wf`). Same registry + serializer.
const ROSTER_FILTER_PARAM = 'rf';

/** Read the roster filters[] from the URL, dropping unknown axes. */
export function readRosterFiltersFromUrl() {
  if (typeof window === 'undefined') return [];
  const params = new URLSearchParams(window.location.search);
  return paramToFilters(params.get(ROSTER_FILTER_PARAM))
    .filter((f) => WITNESS_AXES[f.axis]);
}

/** Write the roster filters[] to the URL via replaceState (no reload). */
export function writeRosterFiltersToUrl(filters) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const serialized = filtersToParam(filters);
  if (serialized) params.set(ROSTER_FILTER_PARAM, serialized);
  else params.delete(ROSTER_FILTER_PARAM);
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}

/** Apply the active filters[] array against a witness list.
 *  Semantics: AND across axes, OR within an axis (multi-value).
 *  Mirrors applyAxisFilters from inventoryFilterAxes.
 */
export function applyWitnessFilters(rows, filters, ctx = {}) {
  if (!filters || filters.length === 0) return rows;

  const byAxis = {};
  for (const c of filters) {
    if (!byAxis[c.axis]) byAxis[c.axis] = [];
    byAxis[c.axis].push(c.value);
  }

  let result = rows;
  for (const [axis, values] of Object.entries(byAxis)) {
    const def = WITNESS_AXES[axis];
    if (!def) continue;
    // OR-within-axis.
    result = result.filter((r) => values.some((v) => def.matches(r, v, ctx)));
  }
  return result;
}

/** Pretty-print a chip: "Type: Officer". */
export function formatWitnessChipLabel(filter, ctx = {}) {
  const def = WITNESS_AXES[filter.axis];
  if (!def) return `${filter.axis}: ${filter.value}`;
  const value = def.formatValue ? def.formatValue(filter.value, ctx) : filter.value;
  return `${def.label}: ${value}`;
}

/** Contextual picker rows for the witness rail — same facet-exclusion algorithm
 *  as inventory's getContextualPickerRows: each axis's counts reflect the rows
 *  filtered by chips on OTHER axes, so an axis's own values aren't zeroed.
 *
 *  Signature matches inventory's so it can be injected into AddFilterPicker.
 */
export function getWitnessPickerRows(facetRows, activeFilters, ctx = {}) {
  const rows = [];
  for (const axisKey of WITNESS_AXIS_ORDER) {
    const def = WITNESS_AXES[axisKey];
    if (!def) continue;
    if (def.visibleWhen && !def.visibleWhen(ctx)) continue;

    const possibleValues = def.getValues(facetRows, ctx) || [];
    const otherChips = (activeFilters || []).filter((f) => f.axis !== axisKey);
    const subset = applyWitnessFilters(facetRows, otherChips, ctx);

    for (const v of possibleValues) {
      const count = subset.reduce(
        (n, r) => n + (def.matches(r, v.value, ctx) ? 1 : 0),
        0,
      );
      rows.push({
        axis: axisKey,
        axisLabel: def.label,
        axisIcon: def.icon,
        value: v.value,
        label: v.label,
        count,
        group: v.group,
      });
    }
  }
  return rows;
}
